import { AnimatePresence, motion } from 'framer-motion';

import { useCurrentNode, useNarrativeState, useUpdateNode } from '../store/hooks';

interface VariantEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  worldState: 'good' | 'bad';
}

export function VariantEditorModal({ isOpen, onClose, worldState }: VariantEditorModalProps) {
  const narrativeState = useNarrativeState();
  const currentNode = useCurrentNode();
  const updateNode = useUpdateNode();
  const currentNodeId = narrativeState.currentNodeId;

  // Get Node2 choices to display
  const node2 = narrativeState.nodes['S1_Player_Declaration'];
  const node2Choices = node2?.choices || [];

  const isGoodWorld = worldState === 'good';

  const handleVariantLineChange = (choiceId: string, value: string) => {
    const worldKey = isGoodWorld ? 'goodWorld' : 'badWorld';
    const currentWorldVariants = currentNode?.variantContent?.[worldKey]?.variants || [];

    const updatedVariants = currentWorldVariants.map((v) =>
      v.choiceId === choiceId ? { ...v, variantLine: value } : v
    );

    // If variant doesn't exist, create it
    if (!updatedVariants.find((v) => v.choiceId === choiceId)) {
      const choice = node2Choices.find((c) => c.id === choiceId);
      if (choice) {
        updatedVariants.push({
          choiceId: choiceId,
          choiceText: choice.text,
          variantLine: value,
        });
      }
    }

    updateNode(currentNodeId, {
      variantContent: {
        ...currentNode?.variantContent,
        [worldKey]: {
          variants: updatedVariants,
          placeholderPosition: currentNode?.variantContent?.[worldKey]?.placeholderPosition || 0,
        },
      },
    });
  };

  // Check completion status
  const worldKey = isGoodWorld ? 'goodWorld' : 'badWorld';
  const currentWorldVariants = currentNode?.variantContent?.[worldKey]?.variants || [];

  const emptyVariants = node2Choices
    .map((choice, idx) => {
      const variant = currentWorldVariants.find((v) => v.choiceId === choice.id);
      return !variant?.variantLine || variant.variantLine.trim() === '' ? idx + 1 : null;
    })
    .filter((idx) => idx !== null);

  const isComplete = emptyVariants.length === 0;

  const handleClose = () => {
    if (isComplete) {
      onClose();
    }
  };

  if (!currentNode) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-gradient-to-b from-[#050614] to-[#0f1a2f] border border-[#1a2d4d] rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-b border-[#1a2d4d] px-8 py-6">
              <div>
                <h2
                  className={`text-3xl font-bold uppercase bg-gradient-to-r ${isGoodWorld ? 'from-green-400 to-emerald-500' : 'from-red-400 to-rose-500'} bg-clip-text text-transparent`}
                >
                  Define Variant Lines ({isGoodWorld ? 'Better World' : 'Worse World'})
                </h2>
                <p className="text-sm text-gray-400 mt-2">
                  Configure how the NPC will reference earlier player choices in the{' '}
                  <span
                    className={`font-semibold ${isGoodWorld ? 'text-green-400' : 'text-red-400'}`}
                  >
                    {isGoodWorld ? 'Better World' : 'Worse World'}
                  </span>{' '}
                  narrative branch
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="max-w-6xl mx-auto space-y-4">
                {/* Top Row: Node2 Preview + Instructions */}
                <div className="grid lg:grid-cols-2 gap-4">
                  {/* Node2 Preview Card */}
                  <div className="bg-[#0a0f1f] border border-[#1a2d4d]/50 rounded-lg overflow-hidden">
                    <div className="bg-[#0a0f1f] px-3 py-2 border-b border-[#1a2d4d]/50">
                      <h4 className="text-xs font-medium text-gray-400">
                        Just prior to the B-Story diversion, the player had these choices:
                      </h4>
                    </div>

                    <div className="p-3">
                      {node2?.text && (
                        <div className="mb-2">
                          <div className="text-gray-300 leading-relaxed text-xs bg-[#050914] p-2 rounded border border-[#1a2d4d]/30">
                            {node2.text.length > 120
                              ? `${node2.text.substring(0, 120)}...`
                              : node2.text}
                          </div>
                        </div>
                      )}

                      {node2Choices.length > 0 &&
                        node2Choices.some((choice) => choice.text.trim()) && (
                          <div className="space-y-1">
                            {node2Choices.map(
                              (choice, index) =>
                                choice.text.trim() && (
                                  <div
                                    key={choice.id}
                                    className="bg-[#1a2d2f] border border-[#059669]/30 text-gray-300 rounded px-2 py-1 text-xs"
                                  >
                                    <span className="text-gray-400">Choice {index + 1}:</span>{' '}
                                    {choice.text.length > 40
                                      ? `${choice.text.substring(0, 40)}...`
                                      : choice.text}
                                  </div>
                                )
                            )}
                          </div>
                        )}

                      {!node2?.text && !node2Choices?.some((choice) => choice.text.trim()) && (
                        <p className="text-xs text-gray-500 italic text-center py-4">
                          No content yet in Node 2
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="bg-[#0c1222] border border-[#1a2d4d] rounded-lg p-4">
                    <div className="text-gray-300 text-sm space-y-3">
                      <p>
                        Consider that the player choosing any of those options was the moment at
                        which the NPC changed the subject for their story. Now, consider that we are
                        in the{' '}
                        <span
                          className={`font-semibold ${isGoodWorld ? 'text-green-400' : 'text-red-400'}`}
                        >
                          {isGoodWorld ? 'Better World' : 'Worse World'}
                        </span>{' '}
                        {`where the NPC will frame the player's earlier choice with a `}{' '}
                        <span
                          className={`font-semibold ${isGoodWorld ? 'text-green-400' : 'text-red-400'}`}
                        >
                          {isGoodWorld ? 'hopeful/optimistic' : 'bitter/pessimistic'}
                        </span>{' '}
                        perspective.
                      </p>
                      <p>
                        Your job here? To figure out a way to make it so that the NPC is going to{' '}
                        <em>reference</em> and <em>remember</em> what the player said earlier,
                        bringing it up somehow -- but in a way that fits the{' '}
                        <span
                          className={`font-semibold ${isGoodWorld ? 'text-green-400' : 'text-red-400'}`}
                        >
                          {isGoodWorld
                            ? 'hopeful tone of the Better World'
                            : 'bitter tone of the Worse World'}
                        </span>{' '}
                        narrative branch.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Variant Text Inputs - Three Columns */}
                <div className="space-y-3">
                  <p className="text-sm text-white">
                    {`In this node, your NPC will at some point say something differently based on the
  earlier choices. Here, you're going to define them:`}
                  </p>
                  <div className="grid lg:grid-cols-3 gap-3">
                    {node2Choices.map((choice, idx) => {
                      const variant = currentWorldVariants.find((v) => v.choiceId === choice.id);
                      return (
                        <div
                          key={choice.id}
                          className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border border-[#1a2d4d] rounded-lg p-4"
                        >
                          <div className="space-y-3">
                            <div className="flex items-start gap-3">
                              <span className="bg-[#4f46e5] text-white px-2 py-1 rounded text-xs font-medium flex-shrink-0">
                                {idx + 1}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-gray-400 mb-2 leading-relaxed">
                                  If earlier, the player chose{' '}
                                  <span className="text-white font-medium bg-[#1a2d4d] px-1 py-0.5 rounded text-xs">
                                    &quot;
                                    {choice.text && choice.text.length > 20
                                      ? `${choice.text.substring(0, 20)}...`
                                      : choice.text || `Choice ${idx + 1}`}
                                    &quot;
                                  </span>
                                  , then now, the NPC will reference it by saying:
                                </div>
                                <textarea
                                  value={variant?.variantLine || ''}
                                  onChange={(e) =>
                                    handleVariantLineChange(choice.id, e.target.value)
                                  }
                                  placeholder="Write how the NPC will reference this specific choice..."
                                  className="w-full h-16 bg-[#0a0f1f] border border-[#1a2d4d] rounded p-2 text-xs text-white placeholder-gray-500 resize-none focus:outline-none focus:ring-1 focus:ring-[#4f46e5] focus:border-transparent"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gradient-to-br from-[#0c1222] to-[#0a1020] border-t border-[#1a2d4d] px-8 py-6 flex justify-between items-center">
              {/* Validation message */}
              <div className="flex-1">
                {!isComplete && (
                  <p className="text-sm text-gray-400">
                    Answers Needed For Choices: {emptyVariants.join(', ')}
                  </p>
                )}
              </div>

              {/* Continue button */}
              <button
                onClick={handleClose}
                disabled={!isComplete}
                className={`px-8 py-3 font-semibold rounded-lg transition-all ${
                  isComplete
                    ? 'bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] text-white hover:shadow-lg hover:scale-105'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                }`}
              >
                Continue →
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
